import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';

interface ModalEntry {
  id: string;
  zIndex: number;
}

interface WindowManagerContextValue {
  modals: ModalEntry[];
  register: (id: string) => void;
  unregister: (id: string) => void;
  bringToFront: (id: string) => void;
  getZIndex: (id: string) => number;
}

const WindowManagerContext = createContext<WindowManagerContextValue | null>(
  null,
);

export const useWindowManager = () => {
  const ctx = useContext(WindowManagerContext);
  if (!ctx)
    throw new Error(
      'useWindowManager must be used inside WindowManagerProvider',
    );
  return ctx;
};

export const useWindow = (id: string) => {
  const { register, unregister, bringToFront, getZIndex } = useWindowManager();

  useEffect(() => {
    register(id);
    return () => unregister(id);
  }, [id, register, unregister]);

  return {
    zIndex: getZIndex(id),
    focus: () => bringToFront(id),
  };
};

export const WindowManagerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [modals, setModals] = useState<ModalEntry[]>([]);

  const defaultIndex = 10;

  const register = useCallback((id: string) => {
    setModals((prev) => {
      if (prev.find((m) => m.id === id)) return prev;
      const max = prev.length
        ? Math.max(...prev.map((m) => m.zIndex))
        : defaultIndex;
      return [...prev, { id, zIndex: max + 1 }];
    });
  }, []);

  const unregister = useCallback((id: string) => {
    setModals((prev) => prev.filter((m) => m.id !== id));
  }, []);

  const bringToFront = useCallback((id: string) => {
    setModals((prev) => {
      const sorted = [
        ...prev.filter((m) => m.id !== id),
        ...prev.filter((m) => m.id === id),
      ];

      return sorted.map((m, i) => ({
        ...m,
        zIndex: defaultIndex + i,
      }));
    });
  }, []);

  const getZIndex = useCallback(
    (id: string) => modals.find((m) => m.id === id)?.zIndex ?? 100,
    [modals],
  );

  return (
    <WindowManagerContext.Provider
      value={{ modals, register, unregister, bringToFront, getZIndex }}
    >
      {children}
    </WindowManagerContext.Provider>
  );
};
